import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, CalendarCheck, Building2, Users, UserPlus, Target, Kanban,
  CheckSquare, Megaphone, Globe2, Briefcase, FileText, BarChart3, Sparkles,
  Zap, Settings, ChevronLeft, ChevronRight, Upload, CalendarDays, X,
} from "lucide-react";
import api from "../lib/api";

const GROUPS = [
  {
    title: "Operatività",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
      { to: "/command-center", label: "Command Center", icon: CalendarCheck, id: "command-center" },
    ],
  },
  {
    title: "Core CRM",
    items: [
      { to: "/aziende", label: "Aziende", icon: Building2, id: "aziende", badge: "companies" },
      { to: "/contatti", label: "Contatti", icon: Users, id: "contatti", badge: "contacts" },
      { to: "/lead", label: "Lead", icon: UserPlus, id: "lead", badge: "leads" },
      { to: "/opportunita", label: "Opportunità", icon: Target, id: "opportunita", badge: "opportunities" },
      { to: "/pipeline", label: "Pipeline", icon: Kanban, id: "pipeline" },
      { to: "/attivita", label: "Attività", icon: CheckSquare, id: "attivita", badge: "activities_due", warn: true },
      { to: "/calendario", label: "Calendario", icon: CalendarDays, id: "calendario" },
    ],
  },
  {
    title: "Business Development",
    items: [
      { to: "/campagne", label: "Campagne", icon: Megaphone, id: "campagne" },
      { to: "/progetti", label: "Progetti", icon: Globe2, id: "progetti" },
      { to: "/clienti", label: "Clienti", icon: Briefcase, id: "clienti" },
      { to: "/documenti", label: "Documenti", icon: FileText, id: "documenti" },
      { to: "/report", label: "Report", icon: BarChart3, id: "report" },
    ],
  },
  {
    title: "Piattaforma",
    items: [
      { to: "/ai-assistant", label: "AI Assistant", icon: Sparkles, id: "ai-assistant" },
      { to: "/automazioni", label: "Automazioni", icon: Zap, id: "automazioni" },
      { to: "/import", label: "Import Center", icon: Upload, id: "import" },
      { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "impostazioni" },
    ],
  },
];

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, onClose }) {
  const [counts, setCounts] = useState({});

  useEffect(() => {
    const load = () => api.get("/meta/counts").then(({ data }) => setCounts(data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  // Mobile/tablet portrait (<lg): off-canvas drawer, always expanded. Desktop (≥lg): sticky, collapsible.
  const showLabels = !collapsed || mobileOpen;
  return (
    <>
      {mobileOpen && <div data-testid="sidebar-backdrop" onClick={onClose} className="fixed inset-0 z-40 bg-navy-900/60 backdrop-blur-[2px] lg:hidden" />}
      <aside
        data-testid="sidebar"
        data-state={mobileOpen ? "open" : "closed"}
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] lg:max-w-none lg:sticky lg:top-0 lg:z-auto ${collapsed ? "lg:w-[68px]" : "lg:w-64"} shrink-0 bg-navy-900 text-slate-300 flex flex-col h-screen h-[100dvh] transition-[transform,width] duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        <div className="h-16 flex items-center gap-2.5 px-4 border-b border-white/10">
          <div className="w-9 h-9 rounded-lg bg-teal-500 flex items-center justify-center shrink-0 font-heading font-bold text-navy-900 text-lg">F</div>
          {showLabels && (
            <div className="leading-tight overflow-hidden flex-1">
              <p className="font-heading font-bold text-white text-sm tracking-tight">Ferranti & Partners</p>
              <p className="text-[10px] text-teal-400 uppercase tracking-widest">Business Development CRM</p>
            </div>
          )}
          <button data-testid="sidebar-mobile-close" onClick={onClose} aria-label="Chiudi menu" className="lg:hidden w-10 h-10 -mr-2 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5"><X className="w-5 h-5" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-4 kanban-scroll">
          {GROUPS.map((g) => (
            <div key={g.title}>
              {showLabels && <p className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{g.title}</p>}
              <div className="space-y-0.5">
                {g.items.map((it) => {
                  const Icon = it.icon;
                  const badgeVal = it.badge ? counts[it.badge] : null;
                  return (
                    <NavLink
                      key={it.id}
                      to={it.to}
                      data-testid={`sidebar-nav-${it.id}`}
                      title={it.label}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-2.5 py-2.5 lg:py-2 min-h-[44px] lg:min-h-0 rounded-lg text-sm transition-colors relative group ${
                          isActive ? "bg-teal-500/15 text-teal-300 font-medium" : "hover:bg-white/5 hover:text-white"
                        }`
                      }
                    >
                      <Icon className="w-[18px] h-[18px] shrink-0" />
                      {showLabels && <span className="truncate flex-1">{it.label}</span>}
                      {showLabels && badgeVal ? (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${it.warn ? "bg-amber-500/20 text-amber-300" : "bg-white/10 text-slate-300"}`}>{badgeVal}</span>
                      ) : null}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <button
          data-testid="sidebar-collapse-toggle"
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex h-11 border-t border-white/10 items-center justify-center text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>
    </>
  );
}
